import React from 'react'
import styled from 'styled-components'

import useAnimatedNumber from 'utils/hooks/useAnimatedNumber'

const BEHAVIORAL_RISK_VALUES = {
  low: 17,
  medium: 50,
  high: 83,
}

const DEFAULT_STATUS = 'high'

const NEEDLE_CENTER_X = 59.524
const NEEDLE_CENTER_Y = 60
const NEEDLE_HALF_WIDTH = 0.42

const GAUGE_ARC_CENTER_X = 57.8
const GAUGE_ARC_CENTER_Y = 67.7
const GAUGE_ARC_INNER_RADIUS = 41

const ARC_START_COMPASS = -150
const ARC_END_COMPASS = 150

const ACTIVE_GLOW_OPACITY = 0.75
const INACTIVE_GLOW_OPACITY = 0.3
const ACTIVE_SECTION_OPACITY = 1
const INACTIVE_SECTION_OPACITY = 0.3

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const getNeedleLength = (compassDeg) => {
  const rad = (compassDeg * Math.PI) / 180
  const dx = Math.sin(rad)
  const dy = -Math.cos(rad)
  const a = NEEDLE_CENTER_X - GAUGE_ARC_CENTER_X
  const b = NEEDLE_CENTER_Y - GAUGE_ARC_CENTER_Y
  const beta = a * dx + b * dy
  const gamma = a * a + b * b - GAUGE_ARC_INNER_RADIUS ** 2
  const disc = beta * beta - gamma
  if (disc < 0) return GAUGE_ARC_INNER_RADIUS
  return -beta + Math.sqrt(disc)
}

const normalizeStatus = (status) => {
  const normalized = String(status || DEFAULT_STATUS).toLowerCase()
  return BEHAVIORAL_RISK_VALUES[normalized] ? normalized : DEFAULT_STATUS
}

const getGlowOpacity = (currentStatus, sectionStatus) =>
  currentStatus === sectionStatus ? ACTIVE_GLOW_OPACITY : INACTIVE_GLOW_OPACITY

const getSectionOpacity = (currentStatus, sectionStatus) =>
  currentStatus === sectionStatus ? ACTIVE_SECTION_OPACITY : INACTIVE_SECTION_OPACITY

const GaugeContainer = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  flex: 0 0 auto;
`

const GaugeSvg = styled.svg`
  position: absolute;
  top: -9.52px;
  left: -9.52px;
  width: 119.048px;
  height: 111.232px;
  overflow: visible;
  display: block;
  pointer-events: none;
`

/**
 * Behavioral-risk needle gauge shared by the Phishing Simulation Agent banner
 * and the Red Team risk widgets. `status` is one of low | medium | high; pass
 * `testIdPrefix` to apply the PSIMA QA data-testids.
 */
const BehavioralRiskGauge = ({ status, testIdPrefix }) => {
  const currentStatus = normalizeStatus(status)
  const targetValue = clamp(BEHAVIORAL_RISK_VALUES[currentStatus], 0, 100)
  const animatedValue = useAnimatedNumber(targetValue)
  const needleAngle =
    ARC_START_COMPASS + ((ARC_END_COMPASS - ARC_START_COMPASS) * animatedValue) / 100
  const needleLength = getNeedleLength(needleAngle)

  return (
    <GaugeContainer data-testid={testIdPrefix ? `${testIdPrefix}_Gauge_div` : undefined}>
      <GaugeSvg viewBox="0 0 119.048 111.232" aria-hidden="true">
        <defs>
          <filter
            id="behavioralRiskGreenGlow"
            x="0"
            y="20.9755"
            width="45.9505"
            height="90.2566"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur stdDeviation="4.7619" />
          </filter>
          <filter
            id="behavioralRiskYellowGlow"
            x="13.6782"
            y="0"
            width="91.5746"
            height="38.778"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur stdDeviation="4.7619" />
          </filter>
          <filter
            id="behavioralRiskRedGlow"
            x="74.0764"
            y="19.9954"
            width="44.9712"
            height="90.5685"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur stdDeviation="4.7619" />
          </filter>
        </defs>

        <g opacity={getGlowOpacity(currentStatus, 'low')} filter="url(#behavioralRiskGreenGlow)">
          <path
            d="M36.0238 100.227C35.1954 101.662 33.3548 102.161 31.9722 101.248C26.9011 97.8994 22.4756 93.6495 18.9196 88.7009C14.7793 82.939 11.9127 76.3619 10.5103 69.4067C9.10783 62.4515 9.20175 55.2774 10.7858 48.3613C12.1462 42.4214 14.579 36.7886 17.9562 31.7365C18.877 30.359 20.7672 30.1059 22.0869 31.1076C23.4066 32.1094 23.655 33.9861 22.7455 35.371C19.8767 39.7394 17.8046 44.591 16.6343 49.7008C15.2404 55.787 15.1578 62.1001 16.3919 68.2207C17.626 74.3413 20.1486 80.1292 23.7921 85.1996C26.8511 89.4567 30.6414 93.1262 34.9788 96.0415C36.3539 96.9658 36.8522 98.7921 36.0238 100.227Z"
            fill="#09DE85"
          />
        </g>
        <g
          opacity={getGlowOpacity(currentStatus, 'medium')}
          filter="url(#behavioralRiskYellowGlow)"
        >
          <path
            d="M24.2141 28.504C22.9694 27.4105 22.8406 25.5078 24.0068 24.3309C28.2841 20.0142 33.3188 16.5072 38.8682 13.9898C45.3297 11.0587 52.341 9.53632 59.4362 9.52389C66.5313 9.51145 73.5479 11.0092 80.0196 13.9177C85.5778 16.4156 90.6248 19.9049 94.9172 24.2066C96.0876 25.3794 95.9654 27.2825 94.7245 28.3804C93.4836 29.4783 91.5946 29.3541 90.4147 28.1909C86.6931 24.5218 82.3416 21.5392 77.5601 19.3904C71.865 16.831 65.6904 15.5129 59.4467 15.5239C53.2029 15.5348 47.033 16.8745 41.3469 19.4539C36.573 21.6195 32.2319 24.6173 28.5232 28.2994C27.3475 29.4667 25.4589 29.5975 24.2141 28.504Z"
            fill="#FFCA1B"
          />
        </g>
        <g opacity={getGlowOpacity(currentStatus, 'high')} filter="url(#behavioralRiskRedGlow)">
          <path
            d="M96.2329 30.1734C97.527 29.1388 99.4229 29.3442 100.378 30.698C103.882 35.6634 106.456 41.2331 107.965 47.1369C109.723 54.0109 109.998 61.1803 108.771 68.1686C107.544 75.157 104.844 81.8042 100.85 87.6686C97.4201 92.7052 93.1031 97.0653 88.1179 100.541C86.7587 101.488 84.9062 101.036 84.0419 99.622C83.1775 98.2085 83.6297 96.3701 84.9811 95.4115C89.2437 92.3878 92.9403 88.624 95.891 84.2912C99.4056 79.1306 101.782 73.281 102.861 67.1313C103.941 60.9815 103.699 54.6725 102.152 48.6233C100.854 43.5446 98.6598 38.7468 95.6819 34.4521C94.7378 33.0905 94.9388 31.2081 96.2329 30.1734Z"
            fill="#E44258"
          />
        </g>

        <path
          opacity={getSectionOpacity(currentStatus, 'low')}
          d="M35.7738 100.66C35.0835 101.856 33.5506 102.271 32.3908 101.521C27.1444 98.132 22.5731 93.7852 18.9196 88.7009C14.7793 82.939 11.9127 76.3619 10.5103 69.4067C9.10783 62.4515 9.20175 55.2774 10.7858 48.3613C12.1835 42.2585 14.7132 36.4799 18.2362 31.3222C19.015 30.182 20.5889 29.9706 21.6886 30.8054C22.7884 31.6401 22.9969 33.2047 22.2245 34.3492C19.127 38.9385 16.8989 44.0665 15.6596 49.4776C14.234 55.7021 14.1494 62.1587 15.4116 68.4184C16.6738 74.6781 19.2537 80.5975 22.98 85.7832C26.2194 90.2912 30.2606 94.155 34.8945 97.1854C36.0501 97.9411 36.4642 99.4643 35.7738 100.66Z"
          fill="#00E5A0"
        />
        <path
          opacity={getSectionOpacity(currentStatus, 'medium')}
          d="M23.8385 28.174C22.8012 27.2628 22.6946 25.6783 23.6566 24.6878C28.0084 20.2073 33.1666 16.5762 38.8682 13.9898C45.3297 11.0587 52.341 9.53632 59.4362 9.52389C66.5313 9.51145 73.5479 11.0092 80.0196 13.9177C85.7303 16.4841 90.9012 20.097 95.2687 24.5623C96.2341 25.5494 96.1331 27.1342 95.099 28.0491C94.0649 28.964 92.4898 28.8619 91.5189 27.8802C87.6254 23.9435 83.0334 20.7538 77.97 18.4783C72.1455 15.8607 65.8306 14.5127 59.4449 14.5239C53.0593 14.5351 46.7491 15.9052 40.9338 18.5432C35.8784 20.8365 31.2977 24.0422 27.418 27.9926C26.4506 28.9777 24.8758 29.0853 23.8385 28.174Z"
          fill="#FFCA1B"
        />
        <path
          opacity={getSectionOpacity(currentStatus, 'high')}
          d="M96.6234 29.8612C97.7018 28.999 99.2805 29.1707 100.088 30.2909C103.74 35.3582 106.414 41.0712 107.965 47.1369C109.723 54.0109 109.998 61.1803 108.771 68.1686C107.544 75.157 104.844 81.8042 100.85 87.6686C97.326 92.8433 92.8656 97.3039 87.7063 100.825C86.5658 101.603 85.023 101.227 84.3027 100.049C83.5824 98.8706 83.958 97.3375 85.0941 96.553C89.6503 93.4067 93.5928 89.4423 96.7176 84.8541C100.312 79.5762 102.742 73.5937 103.846 67.3042C104.95 61.0147 104.703 54.5622 103.121 48.3756C101.746 42.9974 99.3892 37.9273 96.177 33.4174C95.376 32.2928 95.545 30.7234 96.6234 29.8612Z"
          fill="#E44258"
        />

        <g
          data-testid={testIdPrefix ? `${testIdPrefix}_Needle_g` : undefined}
          transform={`rotate(${needleAngle} ${NEEDLE_CENTER_X} ${NEEDLE_CENTER_Y})`}
        >
          <path
            d={`M ${NEEDLE_CENTER_X - NEEDLE_HALF_WIDTH} ${NEEDLE_CENTER_Y} L ${NEEDLE_CENTER_X} ${
              NEEDLE_CENTER_Y - needleLength
            } L ${NEEDLE_CENTER_X + NEEDLE_HALF_WIDTH} ${NEEDLE_CENTER_Y} Z`}
            fill="#F2F5FE"
          />
          <circle cx={NEEDLE_CENTER_X} cy={NEEDLE_CENTER_Y} r="3.91" fill="#3063FD" />
          <circle cx={NEEDLE_CENTER_X} cy={NEEDLE_CENTER_Y} r="1.83" fill="#F2F5FE" />
        </g>
      </GaugeSvg>
    </GaugeContainer>
  )
}

export default BehavioralRiskGauge
